import type { RefObject } from "react";
import { Info } from "lucide-react";
import { LeaveInsightCards, type LeaveBalanceRow } from "../../LeaveInsightCards.js";
import type { LeaveTypeConfig } from "../../../context/LeaveTypeConfigContext.js";
import { CollapsibleSection } from "../sectionShared.js";

export interface LeaveRequestRow {
  id: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number | string;
  status: string;
  rejectionReason?: string | null;
}

export const leaveStatusStyle: Record<string, React.CSSProperties> = {
  PENDING:   { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" },
  APPROVED:  { background: "var(--color-success-bg)", color: "var(--color-success-fg)" },
  REJECTED:  { background: "var(--color-error-bg)", color: "var(--color-error-fg)" },
  CANCELLED: { background: "var(--color-surface-100)", color: "var(--color-surface-500)" },
};

export function fmtLeaveDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function monthsBetween(from: Date, to: Date): number {
  return Math.max(0, (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()));
}

// Anchored to Jan 1 of the balance year, matching leave-policy.service.ts's own
// tier-matching reference date -- "today's" tenure would disagree with the tier that
// actually produced the numbers below for anyone who crossed a 12/24-month boundary
// after Jan 1, which would look like a bug rather than explain one.
function tenureLabel(hireDateStr: string): string {
  const hireDate = new Date(hireDateStr);
  const year = new Date().getFullYear();
  const months = monthsBetween(hireDate, new Date(Date.UTC(year, 0, 1)));
  return `${months} months' tenure as of Jan 1, ${year} (hired ${fmtLeaveDate(hireDateStr)} ${hireDate.getFullYear()})`;
}

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  loading: boolean;
  balance: LeaveBalanceRow[];
  leaveTypeLabels: Record<string, string>;
  requests: LeaveRequestRow[];
  typeConfig?: Record<string, LeaveTypeConfig> | undefined;
  gender?: string | null | undefined;
  maritalStatus?: string | null | undefined;
  hireDate?: string | null | undefined;
}

export function LeaveSection({ sectionRef, loading, balance, leaveTypeLabels, requests, typeConfig, gender, maritalStatus, hireDate }: Props) {
  return (
    <CollapsibleSection id="leave" sectionRef={sectionRef} title="Leave">
      {loading ? (
        <p style={{ fontSize: 13, color: "var(--color-surface-500)" }}>Loading…</p>
      ) : (
        <>
          {hireDate && (
            <div className="mb-3 flex items-center gap-1.5 text-[11px] text-surface-500">
              <Info size={13} className="shrink-0" />
              {tenureLabel(hireDate)}
            </div>
          )}
          {balance.length > 0 ? (
            <LeaveInsightCards balance={balance} labels={leaveTypeLabels} typeConfig={typeConfig} gender={gender} maritalStatus={maritalStatus} />
          ) : (
            <p style={{ fontSize: 13, color: "var(--color-surface-500)", marginBottom: 16 }}>No tracked leave balance for this employee.</p>
          )}

          <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 8, marginBottom: 10 }}>
            Recent Requests
          </h4>
          {requests.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--color-surface-500)" }}>No leave requests yet.</p>
          ) : (
            <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Type", "Dates", "Days", "Status"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: i < requests.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                      <td style={{ padding: "8px 12px", fontSize: 13 }}>{r.leaveType.replace(/_/g, " ")}</td>
                      <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--color-surface-700)" }}>
                        {r.startDate === r.endDate ? fmtLeaveDate(r.startDate) : `${fmtLeaveDate(r.startDate)} – ${fmtLeaveDate(r.endDate)}`}
                      </td>
                      <td style={{ padding: "8px 12px", fontSize: 13 }}>
                        {Number(r.days) % 1 === 0 ? Number(r.days) : Number(r.days).toFixed(1)}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ ...leaveStatusStyle[r.status], padding: "2px 8px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </CollapsibleSection>
  );
}
